import { getSupabaseClient } from '@lingaai/supabase-client';
import type { User, UserPermissions } from '../types';

export async function updateUserProfile(
  userId: string,
  data: {
    first_name?: string;
    last_name?: string;
    organization?: string;
  }
): Promise<User> {
  const supabase = getSupabaseClient();

  const { data: updated, error } = await supabase.from('users').update(data).eq('id', userId).select().single();

  if (error) throw error;
  return updated as User;
}

export async function listUsers(tenantId?: string): Promise<User[]> {
  const supabase = getSupabaseClient();

  let query = supabase.from('users').select('*').order('created_at', { ascending: false });

  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as User[];
}

export async function createUser(userData: { email: string; first_name: string; last_name: string; role: string; tenant_id: string; organization?: string }): Promise<User> {
  const supabase = getSupabaseClient();

  // Create auth user first
  const { data: authData, error: authError } = await supabase.auth.admin.createUser({
    email: userData.email,
    email_confirm: true,
  });

  if (authError) throw authError;

  // Create user record
  const { data, error } = await supabase
    .from('users')
    .insert({
      id: authData.user.id,
      ...userData,
    })
    .select()
    .single();

  if (error) throw error;
  return data as User;
}

export async function updateUserRole(userId: string, role: string): Promise<User> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.from('users').update({ role }).eq('id', userId).select().single();

  if (error) throw error;
  return data as User;
}

export async function updateUserPermissions(userId: string, permissions: UserPermissions): Promise<User> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.from('users').update({ user_permissions: permissions }).eq('id', userId).select().single();

  if (error) throw error;
  return data as User;
}

export async function resetUserPassword(userId: string): Promise<void> {
  const response = await fetch(`http://localhost:3000/users/${userId}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ redirect_to: `${window.location.origin}/auth/set-password` }),
  });
  if (!response.ok) throw new Error('Failed to reset password');
}

export async function deactivateUser(userId: string): Promise<void> {
  const response = await fetch(`http://localhost:3000/users/${userId}/deactivate`, {
    method: 'PATCH',
  });
  if (!response.ok) throw new Error('Failed to deactivate user');
}

export async function activateUser(userId: string): Promise<void> {
  const response = await fetch(`http://localhost:3000/users/${userId}/activate`, {
    method: 'PATCH',
  });
  if (!response.ok) throw new Error('Failed to activate user');
}

export async function changeUserRole(userId: string, role: string): Promise<void> {
  const response = await fetch(`http://localhost:3000/users/${userId}/role`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  if (!response.ok) throw new Error('Failed to change role');
}

export async function deleteUser(userId: string): Promise<void> {
  const response = await fetch(`http://localhost:3000/users/${userId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete user');
}

export async function forceLogout(userId: string): Promise<void> {
  const response = await fetch(`http://localhost:3000/users/${userId}/force-logout`, {
    method: 'POST',
  });
  if (!response.ok) throw new Error('Failed to force logout');
}
